
import React, { useRef, useState, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    Divider,
    Slider,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import EchoSoundscapesLogo from "../../assets/echoSoundscape.png";
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useNavigate } from 'react-router-dom';
import { getAudioCategories } from '../../service/audioCategory';
import { getAudioTracks } from '../../service/audioTracks';
import { useTheme, styled } from '@mui/material/styles';


// Animation keyframes
const theme1Sway = keyframes`
    0%, 100% { 
        transform: translateX(-10px) translateY(0px) rotate(-2deg); 
        opacity: 0.6;
    }
    50% { 
        transform: translateX(10px) translateY(-15px) rotate(2deg); 
        opacity: 0.9;
    }
`;

const theme2Drop = keyframes`
    0% { 
        transform: translateY(-100vh) translateX(0px); 
        opacity: 0;
    }
    10% { 
        opacity: 1;
    }
    90% { 
        opacity: 1;
    }
    100% { 
        transform: translateY(100vh) translateX(20px); 
        opacity: 0;
    }
`;

const theme3Float = keyframes`
    0%, 100% { 
        transform: translateY(0px) translateX(0px) rotate(0deg) scale(0.8); 
        opacity: 0.3;
    }
    25% { 
        transform: translateY(-20px) translateX(15px) rotate(90deg) scale(1.2); 
        opacity: 0.8;
    }
    50% { 
        transform: translateY(-10px) translateX(-10px) rotate(180deg) scale(1); 
        opacity: 1;
    }
    75% { 
        transform: translateY(-30px) translateX(5px) rotate(270deg) scale(1.1); 
        opacity: 0.7;
    }
`;

const pulseGlow = keyframes`
    0%, 100% { 
        box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
        transform: scale(1);
    }
    50% { 
        box-shadow: 0 0 40px rgba(255, 255, 255, 0.4);
        transform: scale(1.05);
    }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CardsGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 20,
    maxWidth: '100%',
    width: '100%',
    '@media (min-width: 900px)': { gap: 24 },
    '@media (min-width: 1200px)': { maxWidth: 1200, margin: '0 auto', gap: 28 },
    '@media (max-width: 599px)': { gridTemplateColumns: '1fr', gap: 16 },
});

const TherapyCard = styled(Box)(() => ({
    position: 'relative',
    borderRadius: 20,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
    boxShadow: '0 4px 20px rgba(20,42,48,0.08)',
    background: '#FFFFFF',
    opacity: 0,
    transform: 'translateY(24px)',
    animation: `${slideUp} 0.6s ease-out forwards`,
    '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 24px 60px rgba(20,42,48,0.12)',
        '& img': { transform: 'scale(1.08)' },
    },
}));

const CardInner = styled(Box)({
    display: 'flex',
    height: 240,
    overflow: 'hidden',
});

const CardImage = styled(Box)({
    width: '50%',
    flexShrink: 0,
    overflow: 'hidden',
    position: 'relative',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
    },
});

const CardBody = styled(Box)(({ gradient }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 20px',
    textAlign: 'center',
    position: 'relative',
    background: gradient,
}));

const CardTitle = styled(Typography)({
    fontFamily: '"Inter", sans-serif',
    fontSize: 19,
    fontWeight: 600,
    color: '#fff',
    lineHeight: 1.25,
    marginBottom: 18,
});

const CardButton = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    borderRadius: 50,
    fontSize: 12,
    fontWeight: 600,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.16)',
    backdropFilter: 'blur(8px)',
    color: '#fff',
    letterSpacing: 0.2,
    '& svg': { flexShrink: 0 },
    '@media (max-width: 599px)': { borderRadius: 30 },
});

const EchoSoundscapes = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showSlider, setShowSlider] = useState(false);
    const [isRepeat, setIsRepeat] = useState(false);
    const [isShuffle, setIsShuffle] = useState(false);
    const [volume, setVolume] = useState(1);
    const [favorites, setFavorites] = useState([]);
    const [categories, setCategories] = useState([]);
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const progressRef = useRef(null);
    const currentTimeRef = useRef(null);
    const durationRef = useRef(null);


    // Animation elements state
    const [animationElements, setAnimationElements] = useState([]);

    const categoryRoutes = {
        rejuvenate_echo_sounds: '/rejuvenate-sounds',
        echo_soundscapes: '/echo-soundscapes',
        manage_your_thoughts: '/sound-meditation',
        sleep_stories: '/sleep-stories',
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await getAudioCategories();
                setCategories(response.data || []);
            } catch (error) {
                console.error('Failed to fetch audio categories:', error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const echoCategory = categories.find(c => c.name === 'echo_soundscapes');
        if (!echoCategory?.id) return;
        const fetchTracks = async () => {
            try {
                setLoading(true);
                const response = await getAudioTracks(echoCategory.id);
                setSongs(response.data || []);
            } catch (error) {
                console.error('Failed to fetch echo soundscape tracks:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchTracks();
    }, [categories]);

    console.log("echo soundscapes rendered")

    



    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.src = songs[currentIndex]?.audio_url || '';
        audio.load();

        if (isPlaying) {
            audio.play().catch(() => { });
        }

        const handleTimeUpdate = () => {
            if (!audio.duration) return;

            // Update progress bar width
            if (progressRef.current) {
                progressRef.current.style.width = `${(audio.currentTime / audio.duration) * 100}%`;
            }

            // Update current time display
            if (currentTimeRef.current) {
                currentTimeRef.current.textContent = formatTime(audio.currentTime);
            }

            // Update total duration
            if (durationRef.current) {
                durationRef.current.textContent = formatTime(audio.duration);
            }
        };

        const handleEnded = () => {
            if (isRepeat) {
                audio.currentTime = 0;
                audio.play();
            } else {
                playNext();
            }
        };

        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("ended", handleEnded);

        return () => {
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("ended", handleEnded);
        };
        }, [songs, currentIndex, isRepeat]);
    // }, [songs, currentIndex, isPlaying, isRepeat, isShuffle]);

    // Get background gradient based on current song
    const getBackgroundGradient = () => {
        if (!isPlaying) {
            return 'linear-gradient(135deg, #667eea 0%, #764ba2 30%, #f093fb 70%, #f5576c 100%)';
        }

        const currentTheme = songs[currentIndex]?.theme || 'default';
        switch (currentTheme) {
            case 'theme1':
                return 'linear-gradient(135deg, #22c55e 0%, #16a34a 25%, #15803d 50%, #166534 75%, #14532d 100%)';
            case 'theme2':
                return 'linear-gradient(145deg, #ef5350 0%, #c62828 100%)';
            case 'theme3':
                return 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 25%, #1e40af 50%, #1e3a8a 75%, #172554 100%)';
            default:
                return 'linear-gradient(135deg, #667eea 0%, #764ba2 30%, #f093fb 70%, #f5576c 100%)';
        }
    };



    const playSong = (index) => {
        setCurrentIndex(index);
        setIsPlaying(true);
    };

    // const togglePlayPause = () => {
    //     if (!audioRef.current) return;
    //     if (isPlaying) audioRef.current.pause();
    //     else audioRef.current.play();
    //     setIsPlaying(!isPlaying);
    // };

const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
        audio.play()
            .then(() => setIsPlaying(true))
            .catch((error) => {
                console.error("Audio playback failed:", error);
            });
    } else {
        audio.pause();
        setIsPlaying(false);
    }
};

    const playNext = () => {
        setCurrentIndex((prev) => (prev + 1) % songs.length);
    };

    const playPrev = () => {
        setCurrentIndex((prev) => (prev - 1 + songs.length) % songs.length);
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const toggleFavorite = (index) => {
        setFavorites((prev) =>
            prev.includes(index)
                ? prev.filter((i) => i !== index)
                : [...prev, index]
        );
    };

    const handleSongEnd = () => {
        navigate("/echo-sounds#feedback");
    };

    return (
        <Box>
            <Card
                sx={{
                    height: {
                        xs: 'calc(100vh - 56px)',
                        sm: 'calc(100vh - 64px)',
                        md: 'calc(100vh - 70px)',
                    },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    background: getBackgroundGradient(),
                    color: '#fff',
                    borderRadius: 0,
                    position: 'relative',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    transition: 'background 1s ease-in-out'
                }}
            >
                {/* Animated Background Elements */}
                {animationElements.map((element) => (
                    <Box key={element.id} sx={element.style} />
                ))}

                {/* Pulsing Glow Effect for Playing State */}
                {/* {isPlaying && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                            animation: `${pulseGlow} 3s ease-in-out infinite`,
                            zIndex: 0,
                            pointerEvents: 'none'
                        }}
                    />
                )} */}

                {/* Left Panel */}
                <Box
                    sx={{
                        width: { xs: '100%', md: '40%' },
                        position: 'relative',
                        zIndex: 1,
                        height: '100%',              // fill parent Card height
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',    // vertical center for the icon+text
                        // optional padding from top
                    }}
                >
                    {/* Header at top-left */}
                    <Box sx={{
                        position: 'absolute', top: { xs: 4, sm: 14, md: 16 }, left: { xs: 4, sm: 14, md: 16 }, zIndex: 5, ml: { xs: 2, md: 2 },
                        pt: { xs: 2, md: 0 }
                    }}>
                        <Typography
                            variant="h5"
                            fontWeight={300}
                            sx={{
                                fontSize: {
                                    xs: '1.2rem',  // mobile
                                    sm: '1.3rem',  // tablets
                                    md: '1.5rem',  // desktops
                                }
                            }}
                        >
                            {/* Nature's Echo Awaits */}
                            Echo Soundscapes
                        </Typography>
                    </Box>

                    <Box sx={{
                        position: 'absolute', top: { xs: 4, sm: 14, md: 16 }, right: { xs: 4, sm: 14, md: 16 }, zIndex: 5, ml: { xs: 2, md: 2 },
                        pt: { xs: 2, md: 0 }
                    }}>
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                width: { xs: 24, sm: 30 },
                                height: { xs: 24, sm: 30 },
                                mr: 2,
                                borderRadius: '50%',
                                color: '#ffffff',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: { xs: '18px', sm: '22px' } }} />
                        </IconButton>
                    </Box>


                    {/* Candle and Text */}
                    <CardContent
                        sx={{
                            p: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            width: '100%',
                            mt: { xs: 12, sm: 14, md: 0 }
                        }}
                    >
                        {/* ---- ICON + ANIMATED CIRCLES (wrapper reserves space) ---- */}
                        <Box
                            sx={{
                                width: { xs: '70%', md: '40vw' },
                                // Reserve vertical space so absolutely-positioned circles don't overlap content below
                                height: { xs: 180, md: 220 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                mb: 2, // small gap before description
                                pointerEvents: 'none', // allow clicks to pass through if needed
                            }}
                        >
                            {/* Animated Circles (absolute but inside a container with height) */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: { xs: '60', sm: '180px', md: '220px' },
                                    height: { xs: '60', sm: '180px', md: '220px' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                {[1, 2, 3, 4].map((index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'absolute',
                                            borderRadius: '50%',
                                            border: '2px solid rgba(255, 255, 255, 0.22)',
                                            width: `${index * 60}px`,   // slightly smaller steps to fit container
                                            height: `${index * 60}px`,
                                            animation: isPlaying ? `${pulseGlow} ${2 + index * 0.45}s ease-in-out infinite` : 'none',
                                            animationDelay: `${index * 0.25}s`,
                                            opacity: 0.9,
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Center Icon (on top of circles) */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    zIndex: 3,
                                    width: { xs: 60, sm: 70, md: 84 },
                                    height: { xs: 60, sm: 70, md: 84 },
                                    background: 'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '2rem',
                                    backdropFilter: 'blur(6px)',
                                    pointerEvents: 'auto', // allow interactions if needed
                                }}
                            >
                                {/* {songs[currentIndex].theme === 'theme1' && '🌲'}
                                {songs[currentIndex].theme === 'theme2' && '🌧️'}
                                {songs[currentIndex].theme === 'theme3' && '✨'} */}
                                {isPlaying ? (
                                    <Box
                                        component="img"
                                        src={songs[currentIndex]?.icon_url || EchoSoundscapesLogo}
                                        alt={songs[currentIndex]?.title || 'Echo Soundscapes'}
                                        sx={{
                                            width: { xs: 30, sm: 36, md: 48 },
                                            height: { xs: 30, sm: 36, md: 48 },
                                            objectFit: 'contain',
                                            borderRadius: '50%',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        component="img"
                                        src={EchoSoundscapesLogo}
                                        alt="Echo Soundscapes"
                                        sx={{
                                            width: { xs: 30, sm: 36, md: 48 },
                                            height: { xs: 30, sm: 36, md: 48 },
                                            objectFit: 'contain',
                                            borderRadius: '50%',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Typography
                            variant="body1"
                            sx={{
                                opacity: 0.9,
                                maxWidth: { xs: '92%', md: '80%' },
                                mt: { xs: 4, md: 2 }, // small separation
                                fontSize: {
                                    xs: '1rem',   // mobile
                                    sm: '1.1rem',     // small tablets
                                    md: '1.2rem',   // desktops
                                },
                            }}
                        >
                            Nature's Echo Awaits
                        </Typography>

                        {/* ---- DESCRIPTION (below icon, won't overlap) ---- */}
                        <Typography
                            variant="body1"
                            sx={{
                                opacity: 0.9,
                                maxWidth: { xs: '92%', md: '80%' },
                                mt: { xs: 2, md: 2 }, // small separation
                            }}
                        > Echo Soundscapes blend nature’s harmony with tones designed to calm your mind, center your breath, and uplift your mood. Each soundscape is uniquely curated — from gentle dawn flutes to oceanic murmurs — helping you reconnect with your inner stillness anytime, anywhere.
                            {/* {getCurrentSongDescription()} */}
                        </Typography>
                    </CardContent>
                </Box>

                {/* Right Panel */}
                <CardContent sx={{
                    width: { xs: '100%', md: '60%' },
                    px: { xs: 2, sm: 3, md: 0 },
                    py: { xs: 1, sm: 2, md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    mt: { xs: 0, sm: 0, md: 5 },
                    marginLeft: { xs: 1 },
                    // position: "relative",
                    zIndex: 2
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                textAlign: { xs: 'center', sm: 'left' }
                            }}
                        >
                            echo Recommendations
                        </Typography>

                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                width: { sm: 30, md: 40 },
                                height: { sm: 30, md: 40 },
                                mr: 2,
                                borderRadius: '50%',
                                color: '#ffffff',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)'
                                }
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: { xs: '18px', sm: '22px' } }} />
                        </IconButton>
                    </Box>

                    {/* Track List */}
                    <Box sx={{ flexGrow: 1, mr: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {loading ? (
                            <Typography sx={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', mt: 4 }}>
                                Loading tracks...
                            </Typography>
                        ) : songs.map((song, index) => (
                            <Box
                                key={song.id}
                                onClick={() => { setCurrentIndex(index); setIsPlaying(true); }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: '10px 14px',
                                    borderRadius: '16px',
                                    cursor: 'pointer',
                                    background: currentIndex === index
                                        ? 'rgba(255,255,255,0.28)'
                                        : 'rgba(255,255,255,0.12)',
                                    backdropFilter: 'blur(8px)',
                                    border: currentIndex === index
                                        ? '1px solid rgba(255,255,255,0.5)'
                                        : '1px solid rgba(255,255,255,0.15)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        background: 'rgba(255,255,255,0.22)',
                                        border: '1px solid rgba(255,255,255,0.35)',
                                    },
                                }}
                            >
                                {/* Thumbnail */}
                                <Box
                                    component="img"
                                    src={song.icon_url}
                                    alt={song.title}
                                    loading="lazy"
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '10px',
                                        objectFit: 'cover',
                                        flexShrink: 0,
                                    }}
                                />
                                {/* Text */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                        color: '#fff',
                                        lineHeight: 1.3,
                                        mb: 0.4,
                                    }}>
                                        {song.title}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: { xs: '0.72rem', sm: '0.8rem' },
                                        color: 'rgba(255,255,255,0.7)',
                                        lineHeight: 1.4,
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                    }}>
                                        {song.description}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Music Controls - Right Aligned */}
                    <Box sx={{
                        mt: { xs: 2, md: 4 },
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        pr: { xs: 2, sm: 4 },
                        mb: 2
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '28px',
                            padding: '4px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        }}>
                            {/* Navigation Controls */}
                            <IconButton
                                onClick={playPrev}
                                size="large"
                                sx={{
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                }}
                            >
                                <SkipPreviousIcon sx={{ fontSize: 28 }} />
                            </IconButton>

                            <IconButton
                                onClick={togglePlayPause}
                                size="large"
                                sx={{
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                }}
                            >
                                {isPlaying ? (
                                    <PauseCircleIcon sx={{ fontSize: 36 }} />
                                ) : (
                                    <PlayCircleIcon sx={{ fontSize: 36 }} />
                                )}
                            </IconButton>

                            <IconButton
                                onClick={playNext}
                                size="large"
                                sx={{
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                    mr: 1
                                }}
                            >
                                <SkipNextIcon sx={{ fontSize: 28 }} />
                            </IconButton>

                            {/* Divider */}
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                    height: 24,
                                    alignSelf: 'center'
                                }}
                            />

                            <IconButton
                                onClick={() => setIsRepeat(!isRepeat)}
                                size="large"
                                sx={{
                                    color: isRepeat ? '#FFD700' : '#fff',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        color: isRepeat ? '#FFD700' : '#fff'
                                    }
                                }}
                            >
                                <ReplayIcon sx={{ fontSize: 24 }} />
                            </IconButton>

                            <IconButton
                                onClick={() => setIsShuffle(!isShuffle)}
                                size="large"
                                sx={{
                                    color: isShuffle ? '#90EE90' : '#fff',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        color: isShuffle ? '#90EE90' : '#fff'
                                    }
                                }}
                            >
                                <ShuffleIcon sx={{ fontSize: 24 }} />
                            </IconButton>

                            <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                                {/* Volume Icon */}
                                <IconButton
                                    onClick={() => setShowSlider((prev) => !prev)}
                                    sx={{ color: volume === 0 ? "#aaa" : "#fff", mr: 1 }}
                                >
                                    <VolumeUpIcon />
                                </IconButton>

                                {/* Vertical Slider */}
                                {showSlider && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            bottom: 40, // adjust depending on layout
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            height: 120,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "rgba(0,0,0,0.5)",
                                            borderRadius: 2,
                                            p: 1,
                                            zIndex: 10,
                                        }}
                                    >
                                        <Slider
                                            orientation="vertical"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={volume}
                                            onChange={(e, val) => {
                                                setVolume(val);
                                                if (audioRef.current) audioRef.current.volume = val;
                                            }}
                                            sx={{
                                                height: 100,
                                                color: "#fff",
                                                "& .MuiSlider-thumb": { width: 12, height: 12 },
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>

                        </Box>
                    </Box>

                    {/* Audio Element (Hidden) */}
                    <audio
                        ref={audioRef}
                        src={songs[currentIndex]?.audio_url || ''}
                        onTimeUpdate={null}
                        onLoadedMetadata={(e) => setDuration(e.target.duration)}
                        // onEnded={playNext}
                        onEnded={handleSongEnd}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Typography variant="caption" sx={{ minWidth: 35 }} ref={currentTimeRef}>
                            0:00
                        </Typography>

                        <Box
                            sx={{
                                flex: 1,
                                height: 5,
                                mx: 2,
                                bgcolor: "rgba(255, 255, 255, 0.3)",   // softer background instead of #333
                                borderRadius: 5,
                                position: "relative",
                                cursor: "default",
                                overflow: "hidden",                     // ensures rounded edges work
                            }}
                        >
                            <Box
                                ref={progressRef}
                                sx={{
                                    height: "100%",
                                    width: duration ? `${(currentTime / duration) * 100}%` : "0%",
                                    background: "linear-gradient(90deg, #667eea, #764ba2)", // gradient instead of plain white
                                    transition: "width 0.1s linear",
                                }}
                            />
                        </Box>

                        <Typography variant="caption" sx={{ minWidth: 35 }} ref={durationRef}>
                            0:00
                        </Typography>
                    </Box>

                </CardContent>
            </Card>
        </Box>
    );
};

export { EchoSoundscapes };